import { toast } from 'sonner';

import { supabase } from "@/integrations/supabase/client";

// Define available languages
export type Language = {
  code: string;
  name: string;
};

// Get available languages
export const getAvailableLanguages = (): Language[] => {
  return [
    { code: 'sv', name: 'Svenska' },
    { code: 'en', name: 'Engelska' },
    { code: 'pl', name: 'Polska' },
    { code: 'de', name: 'Tyska' },
    { code: 'ru', name: 'Ryska' },
    { code: 'es', name: 'Spanska' },
  ];
};

// Rate limiting with a queue system
const MAX_CONCURRENT_REQUESTS = 2;
const REQUEST_INTERVAL = 1200; // 1.2 seconds between requests
const MAX_RETRIES = 3;
const BACKOFF_MULTIPLIER = 2;

// Track ongoing requests
let activeRequests = 0;
let requestQueue: Array<() => void> = [];
let lastTranslationTime = 0;

/**
 * Manages the request queue to prevent too many concurrent requests
 */
const executeQueuedRequest = () => {
  if (requestQueue.length > 0 && activeRequests < MAX_CONCURRENT_REQUESTS) {
    const request = requestQueue.shift();
    if (request) {
      activeRequests++;
      request();
    }
  }
};

/**
 * Generic queued request function to handle different return types
 */
const queueGenericRequest = <T>(
  requestFn: () => Promise<T>,
  resolve: (value: T | PromiseLike<T>) => void,
  reject: (reason: any) => void
) => {
  const executeRequest = async () => {
    try {
      // Ensure minimum time between requests
      const now = Date.now();
      const timeSinceLastRequest = now - lastTranslationTime;
      
      if (timeSinceLastRequest < REQUEST_INTERVAL) {
        await new Promise(r => setTimeout(r, REQUEST_INTERVAL - timeSinceLastRequest));
      }
      
      lastTranslationTime = Date.now();
      const result = await requestFn();
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      activeRequests--;
      setTimeout(executeQueuedRequest, 50); // Small delay before checking queue again
    }
  };
  
  requestQueue.push(executeRequest);
  executeQueuedRequest();
};

/**
 * Adds a translation request to the queue
 */
const queueTranslationRequest = (
  requestFn: () => Promise<string>,
  resolve: (value: string | PromiseLike<string>) => void,
  reject: (reason: any) => void
) => {
  return queueGenericRequest(requestFn, resolve, reject);
};

// Batch translation function for improved performance
export const translateTextBatch = async (texts: string[], targetLang: string, model?: string): Promise<Record<string, string>> => {
  // If target language is Swedish (our default), return original texts
  if (targetLang === 'sv') {
    const results: Record<string, string> = {};
    texts.forEach(text => {
      results[text] = text;
    });
    return results;
  }

  // Filter out empty texts
  const validTexts = texts.filter(text => text.trim().length > 0);
  if (validTexts.length === 0) {
    const results: Record<string, string> = {};
    texts.forEach(text => {
      results[text] = text;
    });
    return results;
  }

  return new Promise((resolve, reject) => {
    queueGenericRequest(
      async () => {
        const selectedModel = (typeof model === 'string' && model.trim().length > 0) ? model.trim() : 'gpt-4o-mini';
        
        const { data: fnData, error: fnError } = await supabase.functions.invoke('translate-batch-with-ai', {
          body: {
            texts: validTexts,
            targetLang,
            sourceLang: 'sv',
            model: selectedModel,
          },
        });

        if (fnError) {
          console.error('Batch translation function error:', fnError);
          throw new Error(`[BatchEdge] ${fnError.message || JSON.stringify(fnError)}`);
        }

        const translations = fnData?.translations || {};
        
        // Ensure all original texts have results (fallback to original for missing)
        const results: Record<string, string> = {};
        texts.forEach(text => {
          results[text] = translations[text] || text;
        });

        return results;
      },
      resolve,
      reject
    );
  });
};

// Function to translate text using OpenAI API (kept for backward compatibility)
export const translateText = async (text: string, targetLang: string, model?: string): Promise<string> => {
  // If target language is Swedish (our default), return the original text
  if (targetLang === 'sv') {
    return text;
  }
  
  // Skip translation only for empty text
  if (!text.trim()) {
    return text;
  }
  
  // 1) Try DB cache first (shared across users)
  try {
    const { data: cacheHit } = await supabase
      .from('content_translations')
      .select('translated_text')
      .eq('source_lang', 'sv')
      .eq('target_lang', targetLang)
      .eq('source_text', text)
      .maybeSingle();
    if (cacheHit?.translated_text) {
      return cacheHit.translated_text as string;
    }
  } catch (e) {
    console.warn('DB cache lookup failed', e);
  }
  
  // 2) For single text, use batch function for consistency
  try {
    const batchResult = await translateTextBatch([text], targetLang, model);
    return batchResult[text] || text;
  } catch (error) {
    console.error('Translation error:', error);
    
    // Fallback to original text on failure
    try {
      const canToast = typeof window !== 'undefined' && (localStorage.getItem('translation_debug_toasts') === '1' || localStorage.getItem('translate_debug') === '1');
      if (canToast) {
        toast.error(`Översättning misslyckades (${targetLang}). Visar original. ${error instanceof Error ? error.message : ''}`);
      }
    } catch {}
    return text;
  }
};
